<script setup lang="ts">
    import EchoPopover from '../EchoPopover.vue';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { nextTick, onMounted, onUnmounted, ref, watch, computed } from 'vue';
    import { useEditCoachingFormStore } from '@/js/stores/edit-coaching-form';
    import EditCoachingFormSection from './EditCoachingFormSection.vue';
    import ToggleSwitch from '../ToggleSwitch.vue';
    import TextareaInput from '../TextareaInput.vue';
    import InputLabel from '../InputLabel.vue';
    import EditCoachingFormAction from './EditCoachingFormAction.vue';
    import EditCoachingFormReasonCodes from './EditCoachingFormReasonCodes.vue';
    import EditCoachingFormApprovalRating from './EditCoachingFormApprovalRating.vue';
    import EditCoachingFormOpenComment from './EditCoachingFormOpenComment.vue';
    import EditCoachingFormRestrictedComment from './EditCoachingFormRestrictedComment.vue';
    import { storeToRefs } from 'pinia';
    import EchoButton from '../EchoButton.vue';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import ValidationSummary from '../ValidationSummary.vue';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import { ICoachingFormChange } from '@/js/services/api/coachingForm/types';
    import { FORM_LANGUAGE_OPTIONS } from '@/js/consts/form-language-options';
    import {
        AstroidIcon,
        InfoIcon,
        LanguagesIcon,
        PencilIcon,
        PlusIcon,
        RefreshCwIcon,
    } from '@lucide/vue';

    const { setFocus } = useElementFocus();

    const coachingStore = useEditCoachingFormStore();
    const { addSection, registerChange, cleanUp } = coachingStore;
    const { formDraft, sortForm, formInitiated } = storeToRefs(coachingStore);

    const addSectionAndSetFocus = () => {
        const section = addSection();
        setFocus(`#section-title-${section.id}`);
    };

    onMounted(() => {
        nextTick(() => {
            formInitiated.value = true;
        });
    });

    onUnmounted(() => {
        cleanUp();
    });

    // TODO: Register detailed changed if needed, for now just register 'Other' for everything except title.
    // Improve later if needed.
    const watchMap = [
        [() => formDraft.value?.title, 'Title'],
        [() => formDraft.value?.language, 'Other'],
        [() => formDraft.value?.useAi, 'Other'],
        [() => formDraft.value?.prompt, 'Other'],

        [() => formDraft.value?.useAction, 'Other'],
        [() => formDraft.value?.action, 'Other'],
        [() => formDraft.value?.useReasonCodes, 'Other'],
        [() => formDraft.value?.reasonCodes, 'Other'],
        [() => formDraft.value?.useApprovalRating, 'Other'],
        [() => formDraft.value?.approvalRatings, 'Other'],
        [() => formDraft.value?.useOpenComment, 'Other'],
        [() => formDraft.value?.openComment, 'Other'],
        [() => formDraft.value?.useRestrictedComment, 'Other'],
        [() => formDraft.value?.restrictedComment, 'Other'],

        [() => formDraft.value?.sections, 'Other'],
    ] as const satisfies readonly (readonly [() => unknown, ICoachingFormChange])[];

    watchMap.forEach(([getter, changeType]) => {
        watch(
            getter,
            (newVal, oldVal) => {
                if (typeof oldVal !== 'undefined') {
                    registerChange(changeType);
                }
            },
            { deep: true }
        );
    });

    const languageOpen = ref(false);

    const selectedLanguageLabel = computed(() => {
        return __untypedExpression(
            FORM_LANGUAGE_OPTIONS.find((l) => l.value === formDraft.value?.language)?.key || ''
        );
    });

    const languageDropdownRef = ref<HTMLElement | null>(null);

    const handleClickOutside = (event: MouseEvent) => {
        if (!languageOpen.value) return;

        if (
            languageDropdownRef.value &&
            !languageDropdownRef.value.contains(event.target as Node)
        ) {
            languageOpen.value = false;
        }
    };

    const handleKeydown = (event: KeyboardEvent) => {
        if (languageOpen.value && event.key === 'Escape') {
            languageOpen.value = false;
        }
    };

    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleKeydown);
    });
</script>
<template>
    <form v-if="formDraft && formInitiated" class="w-full max-w-235 mx-auto pb-20">
        <ValidationSummary />

        <div class="relative flex items-end w-full max-w-full">
            <div class="flex-grow w-0 max-w-lg">
                <InputLabel for="form-title">
                    {{ __('edit-coaching-form-title') }}
                </InputLabel>
                <TextInput
                    id="form-title"
                    v-model="formDraft.title"
                    :width="TextInputWidth.Full"
                    validation-key="title"
                />
            </div>

            <div class="flex pl-2 items-center gap-2">
                <div ref="languageDropdownRef" class="relative">
                    <button
                        type="button"
                        :aria-expanded="languageOpen"
                        aria-haspopup="listbox"
                        :aria-label="__('select-language')"
                        :class="['flex items-center gap-1 px-3 py-2 text-body-bold cursor-pointer']"
                        @click="languageOpen = !languageOpen"
                    >
                        {{ selectedLanguageLabel }}
                        <LanguagesIcon class="size-4" />
                    </button>
                    <div v-if="languageOpen" class="lang-dropdown" role="listbox">
                        <button
                            v-for="lang in FORM_LANGUAGE_OPTIONS"
                            :key="lang.value"
                            type="button"
                            role="option"
                            :aria-selected="formDraft.language === lang.value"
                            :class="[
                                'block w-full text-left px-2 py-1 text-body rounded hover:bg-gray-lightest',
                                formDraft.language === lang.value
                                    ? 'bg-gray-lightest text-body-bold'
                                    : '',
                            ]"
                            @click="
                                () => {
                                    if (formDraft) {
                                        formDraft.language = lang.value;
                                    }
                                    languageOpen = false;
                                }
                            "
                        >
                            {{ __untypedExpression(lang.key) }}
                        </button>
                    </div>
                </div>

                <!-- <EchoButton
                    variant="tertiary"
                    icon="history.svg"
                    @click="showVersionHistory = formDraft.id"
                >
                    {{ __('version-history') }}
                </EchoButton> -->
                <div>
                    <ToggleSwitch v-model="formDraft.useAi" :start-label="__('use-ai')" />
                </div>
            </div>
        </div>

        <div v-if="formDraft.useAi" class="mt-6">
            <InputLabel :for="'form-prompt'">
                <span class="flex items-center gap-1">
                    <AstroidIcon class="size-4 text-primary-beet" />
                    {{ __('edit-coaching-form-ai-prompt') }}
                </span>
            </InputLabel>
            <TextareaInput id="form-prompt" v-model="formDraft.prompt" :rows="5" />
        </div>

        <div class="flex mt-6 mb-4 justify-between items-end">
            <div>
                <EchoPopover type="text">
                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                        <h2 class="heading-lg flex items-center">
                            {{ __('edit-coaching-form-section') }}

                            <button
                                :ref="anchor"
                                type="button"
                                :aria-label="
                                    __('explain', { label: __('edit-coaching-form-section') })
                                "
                                class="ml-2 text-gray-medium cursor-pointer"
                                v-bind="buttonAttrs"
                                @click="togglePopover"
                            >
                                <InfoIcon class="size-4" />
                            </button>
                        </h2>
                    </template>

                    <template #popover>
                        <p>
                            {{ __('edit-coaching-form-section-description') }}
                        </p>
                    </template>
                </EchoPopover>
            </div>

            <div class="ml-5">
                <EchoButton v-if="!sortForm" variant="tertiary" @click="sortForm = true">
                    {{ __('edit-coaching-form-sort-form') }}
                    <template #icon-right><RefreshCwIcon /></template>
                </EchoButton>

                <EchoButton v-else variant="tertiary" @click="sortForm = false">
                    {{ __('edit-coaching-form-edit-form') }}
                    <template #icon-right><PencilIcon /></template>
                </EchoButton>
            </div>
        </div>

        <TransitionGroup name="animate-section" tag="div" class="relative">
            <EditCoachingFormSection
                v-for="(section, index) in formDraft.sections"
                :key="section.id"
                :section="section"
                :section-index="index"
                :use-ai="formDraft.useAi"
            />
        </TransitionGroup>

        <div v-if="!sortForm" class="pt-1 pb-8">
            <button type="button" class="Add-section-button" @click="addSectionAndSetFocus">
                <PlusIcon class="size-4" />
                {{ __('add-section') }}
            </button>
        </div>

        <div v-if="!sortForm" class="flex flex-col gap-6.5">
            <EditCoachingFormAction />
            <EditCoachingFormReasonCodes />
            <EditCoachingFormApprovalRating />
            <EditCoachingFormOpenComment />
            <EditCoachingFormRestrictedComment />
        </div>
    </form>
</template>

<style scoped>
    :deep(.animate-section-move) {
        transition:
            opacity 0.3s ease-in-out,
            transform 0.3s ease-in-out;
        will-change: transform;
    }

    :deep(.animate-section-enter-active),
    :deep(.animate-section-leave-active) {
        transition: opacity 0.3s ease-in-out;
    }

    :deep(.animate-section-enter-from),
    :deep(.animate-section-leave-to) {
        opacity: 0;
    }
</style>
